import {PlayerId} from '@glark-newco/game-library';
import {Container} from '@pixi/react';
import {IPointData} from 'pixi.js';
import {ReactNode, useMemo, useState} from 'react';
import {CongratulationsAnimation} from './CongratulationsAnimation';
import {GameOverAnimation} from './GameOverAnimation';
import {PlayerSprite} from './PlayerSprite';
import {PlayLink} from './PlayLink';
import {TransferRingPopup} from './TransferRingPopup';
import {useGameTimeExpired} from '../hooks/useGameTimeExpired';
import {useAllObjectivesComplete} from '../hooks/useObjectivesComplete';
import {useIsLinkCreationActive} from '../state/networkEditStore';
import {useNetworkLinks, useNetworkNodes, useNetworkOwnership} from '../state/networkHooks';
import {Link, NodeId} from '../state/networkStore';


export interface PlayerPosition {
    playerId: PlayerId;
    nodeId: NodeId;
    x: number;
    y: number;
}

export interface PopupData extends IPointData {
    playerId: PlayerId;
}

interface UnsortedRows {
    // "y" is always numeric, but TS doesn't like numeric keys.
    [y: string]: PlayerPosition[];
}

export function RemoteCellLayout() {

    const nodes = useNetworkNodes();
    const links = useNetworkLinks();
    const ownership = useNetworkOwnership();

    const objectivesComplete = useAllObjectivesComplete();
    const isTimerExpired = useGameTimeExpired();
    const [popupData, setPopupData] = useState<PopupData | undefined>(undefined);
    const [popupActive, setPopupActive] = useState<PopupData | undefined>(undefined);
    const isLinkCreationActive = useIsLinkCreationActive();

    const linksByY = useMemo(() => {
        return links.filter(l => !!ownership[l.nodeIdA] && !!ownership[l.nodeIdB])
            .map((link: Link) => {
                const nodeA = nodes[link.nodeIdA];
                const nodeB = nodes[link.nodeIdB];
                if (!nodeA || !nodeB) return undefined;

                return {
                    rows: [nodeA.y, nodeB.y].sort(),
                    link,
                };
            })
            .filter(Boolean) as {rows: number[]; link: Link}[];
    }, [links, nodes, ownership]);

    const unsortedRows: UnsortedRows = useMemo(() => {
        const grouped: UnsortedRows = {};
        Object.entries(nodes).forEach(([idStr, node]) => {
            const nodeId = Number(idStr);
            if (!ownership[nodeId]) return;

            const key = String(node.y);
            grouped[key] ??= [];
            grouped[key].push({playerId: ownership[nodeId], nodeId, x: node.x, y: node.y});
        });
        return grouped;
    }, [nodes, ownership]);

    //  Render players and links from back to front
    const playersAndLinks: ReactNode[] = useMemo(() => {
        const elements: ReactNode[] = [];

        const keys = Object.keys(unsortedRows);
        keys.sort().forEach(y => {
            const players: PlayerPosition[] = unsortedRows[y];
            const sameRowLinks: ReactNode[] = linksByY
                .filter(link => link.rows[0] === Number(y) && link.rows[1] === Number(y))
                .map((link, idx) =>
                    <PlayLink link={link.link} key={`link_${link.link.nodeIdA}/${link.link.nodeIdB}_${idx}`}/>);

            const playerSprites: ReactNode[] = players.map((node) =>
                <PlayerSprite playerId={node.playerId} nodeId={node.nodeId} position={{x: node.x, y: node.y}} key={`player_${node.playerId}`} setPopupData={setPopupData}/>);

            const nextRowLinks: ReactNode[] = linksByY
                .filter(link => link.rows[0] === Number(y) && link.rows[1] !== Number(y))
                .map((link, idx) =>
                    <PlayLink link={link.link} key={`link_${link.link.nodeIdA}/${link.link.nodeIdB}_${idx}`}/>);

            elements.push(sameRowLinks, playerSprites, nextRowLinks);
        });
        return elements;
    }, [unsortedRows, linksByY]);

    return (
        <Container x={0} y={0} width={1600} height={1080} >
            {playersAndLinks}
            {(popupData || popupActive) && !isLinkCreationActive && <TransferRingPopup {...popupData!} {...popupActive!} setPopupActive={setPopupActive} anchor={[0, 0.25]} zIndex={100}/>}
            {objectivesComplete && <CongratulationsAnimation/>}
            {isTimerExpired && !objectivesComplete && <GameOverAnimation/>}
        </Container>
    );
}
